import SvgBenefitNotification from '@common/icon/SvgBenefitNotification';
import SvgCoupon from '@common/icon/SvgCoupon';
import SvgCustomPolicy from '@common/icon/SvgCustomPolicy';
import SvgDelivery from '@common/icon/SvgDelivery';
import SvgDirectApply from '@common/icon/SvgDirectApply';
import SvgDonations from '@common/icon/SvgDonations';
import SvgDonationTax from '@common/icon/SvgDonationTax';
import SvgOrders from '@common/icon/SvgOrders';
import SvgPointCoupon from '@common/icon/SvgPointCoupon';
import SvgReview from '@common/icon/SvgReview';
import SvgScrap from '@common/icon/SvgScrap';
import SvgSituationPolicy from '@common/icon/SvgSituationPolicy';
import SvgWelloPoint from '@common/icon/SvgWelloPoint';

import { DONATION_ROUTES, ROUTES, SUB_DOMAIN } from '@/constants';
import { isReachableOnToss } from '@/constants/toss';
import type { MenuAccessData } from '@/stores/useMyPageStore';

type MenuCategory =
  | 'my-wello'
  | 'personal'
  | 'activity'
  | 'customer'
  | 'service-link'
  | 'year-end'
  | 'government';

export interface MenuItem {
  readonly id: string;
  readonly label: string;
  readonly href: string;
  readonly Icon: React.ComponentType<{
    className?: string;
    originalFill?: boolean;
  }>;
  readonly iconBg?: string;
  readonly category: MenuCategory;
  readonly isLoginRequired?: boolean;
  readonly description?: string;
  readonly hiddenSubDomains?: readonly SUB_DOMAIN[];
  readonly visibleSubDomains?: readonly SUB_DOMAIN[];
}

export const MENU_ITEMS: readonly MenuItem[] = [
  {
    id: 'wello-point-receive',
    label: '혜택',
    href: ROUTES.BENEFIT.pathname,
    Icon: SvgWelloPoint,
    category: 'my-wello',
  },
  {
    id: 'scrap',
    label: '스크랩 목록',
    href: ROUTES.MY_SCRAP.pathname,
    Icon: SvgScrap,
    category: 'my-wello',
    hiddenSubDomains: [SUB_DOMAIN.TOSS],
  },
  {
    id: 'product-coupon',
    label: '쿠폰함',
    href: ROUTES.MY_PRODUCT_COUPONS.pathname,
    Icon: SvgCoupon,
    category: 'my-wello',
    hiddenSubDomains: [SUB_DOMAIN.TOSS],
  },
  {
    id: 'coupon',
    label: '쿠폰 구매내역',
    href: ROUTES.POINT_MARKET_HISTORY.pathname,
    Icon: SvgPointCoupon,
    category: 'my-wello',
  },
  {
    id: 'wello-point',
    label: '웰포인트 내역',
    href: ROUTES.POINTS.pathname,
    Icon: SvgWelloPoint,
    category: 'my-wello',
  },
  {
    id: 'jeju-pass',
    label: '탐나는 제주패스',
    href: DONATION_ROUTES.MY_JEJU_RECOMMEND_INFO.pathname,
    Icon: SvgDonationTax,
    category: 'my-wello',
  },
  {
    id: 'marketing-consent',
    label: '마케팅 수신 동의',
    href: ROUTES.MY_NOTIFICATION_SETTINGS.pathname,
    Icon: SvgBenefitNotification,
    category: 'personal',
    //* 토스·KB Pay는 헤더 설정(톱니바퀴)에 갈 수 없어 그 셸에서만 메뉴로 꺼낸다
    visibleSubDomains: [SUB_DOMAIN.TOSS, SUB_DOMAIN.KB_PAY],
  },
  {
    id: 'custom-policy',
    label: '맞춤 정책 설정',
    href: ROUTES.MY_POLICY_FILTER.pathname,
    Icon: SvgCustomPolicy,
    category: 'personal',
    hiddenSubDomains: [SUB_DOMAIN.TOSS],
  },
  {
    id: 'situation-policy',
    label: '상황별 정책 조회',
    href: ROUTES.RECOMMEND_POLICY_SITUATION_MAIN.pathname({ caseType: 'ALL' }),
    Icon: SvgSituationPolicy,
    category: 'activity',
    isLoginRequired: false,
  },
  {
    id: 'donations',
    label: '기부 내역',
    href: DONATION_ROUTES.MY_DONATIONS.pathname,
    Icon: SvgDonations,
    category: 'activity',
  },
  {
    id: 'orders',
    label: '주문 내역',
    href: ROUTES.MY_ORDERS.pathname,
    Icon: SvgOrders,
    category: 'activity',
  },
  {
    id: 'delivery',
    label: '배송지 관리',
    href: ROUTES.MY_DELIVERY_ADDRESS.pathname,
    Icon: SvgDelivery,
    category: 'activity',
  },
  {
    id: 'review',
    label: '내가 남긴 후기',
    href: ROUTES.MY_REVIEWS.pathname,
    Icon: SvgReview,
    category: 'activity',
  },
  {
    id: 'direct-apply',
    label: '바로신청 내역',
    href: ROUTES.MY_APPLY.pathname,
    Icon: SvgDirectApply,
    category: 'activity',
    hiddenSubDomains: [SUB_DOMAIN.TOSS],
  },
];

export const isExternalHref = (href: string) => href.startsWith('http');

export const isReachableHref = (href: string, subDomain?: SUB_DOMAIN | null) =>
  subDomain !== SUB_DOMAIN.TOSS ||
  isExternalHref(href) ||
  isReachableOnToss(href);

const isVisibleMenuItem = (item: MenuItem, subDomain?: SUB_DOMAIN | null) =>
  (!item.visibleSubDomains ||
    (!!subDomain && item.visibleSubDomains.includes(subDomain))) &&
  (!subDomain || !item.hiddenSubDomains?.includes(subDomain)) &&
  isReachableHref(item.href, subDomain);

export const getVisibleMenuItems = (
  items: readonly MenuItem[],
  subDomain?: SUB_DOMAIN | null,
) => items.filter((item) => isVisibleMenuItem(item, subDomain));

export const DEFAULT_QUICK_ICON_IDS: readonly string[] = [
  'custom-policy',
  'direct-apply',
  'orders',
  'delivery',
  'donations',
  'product-coupon',
  'wello-point',
  'review',
];

export const MENU_CATEGORIES: readonly {
  key: MenuCategory;
  label: string;
}[] = [
  { key: 'my-wello', label: '마이 웰로' },
  { key: 'personal', label: '나의 정보' },
  { key: 'activity', label: '나의 활동' },
  { key: 'customer', label: '고객센터' },
  { key: 'service-link', label: '서비스 바로가기' },
  { key: 'year-end', label: '연말정산 혜택' },
  { key: 'government', label: '정부 혜택' },
];

export function getPersonalizedQuickIcons(
  menuAccess: Record<string, MenuAccessData>,
  defaultIds: readonly string[],
  limit = 8,
): string[] {
  const promoted = Object.entries(menuAccess)
    .filter(([, data]) => data.count >= 2)
    .sort(([, a], [, b]) => b.lastAccess - a.lastAccess)
    .map(([id]) => id)
    .slice(0, limit);

  const promotedSet = new Set(promoted);
  const remaining = defaultIds.filter((id) => !promotedSet.has(id));

  return [...promoted, ...remaining].slice(0, limit);
}
